import { useEffect, useState } from "react";
import {
  Title,
  TextInput,
  Textarea,
  NumberInput,
  Button,
  Container,
  Stack,
  Group,
  Text,
  Paper,
  ActionIcon,
  Select,
  MultiSelect,
  Divider,
  Loader,
  Alert,
  Table,
  Badge,
} from "@mantine/core";
import { IconTrash, IconPlus, IconCalculator } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useLocation, useParams } from "wouter";
import { notifications } from "@mantine/notifications";
import ModelSelect from "../_Common/ModelSelect";
import { useTools } from "../Tools/toolStore";
import { getPanel, createPanel, updatePanel, estimatePanel } from "./panelApi";

const PROTOCOL_OPTIONS = [
  { value: "roundtable", label: "Roundtable — answer blind, chair synthesizes" },
  { value: "debate", label: "Debate — blind round 1, then seats rebut each other" },
  { value: "redTeam", label: "Red-team (coming soon)", disabled: true },
];

const ROLE_OPTIONS = [
  { value: "contributor", label: "Contributor" },
  { value: "dissenter", label: "Devil's advocate (steelmans the counter-case)" },
];

const MAX_DEBATE_ROUNDS = 6;

const emptySeat = () => ({
  name: "",
  model: "",
  persona: "",
  temperature: "",
  weight: 1,
  kind: "contributor",
  toolIds: [],
});

function toDto(values) {
  const cleanSeat = (s, kind) => ({
    name: s.name,
    model: s.model,
    persona: s.persona || null,
    temperature: s.temperature === "" || s.temperature == null ? null : Number(s.temperature),
    weight: s.weight ?? 1,
    kind: kind ?? s.kind ?? "contributor",
    // The chair synthesizes the transcript — it doesn't run its own tools.
    toolIds: kind === "chair" || !s.toolIds?.length ? null : s.toolIds,
  });
  return {
    name: values.name,
    description: values.description || null,
    panelists: values.panelists.map((s) => cleanSeat(s, s.kind || "contributor")),
    chair: cleanSeat(values.chair, "chair"),
    protocol: values.protocol,
    rounds: values.rounds ?? 1,
    memory: values.memory ?? "chairSummary",
  };
}

function usd(v) {
  return `$${Number(v).toFixed(2)}`;
}

export default function PanelEditor() {
  const [, setLocation] = useLocation();
  const { panelId } = useParams();
  const isEdit = !!panelId;

  const [loading, setLoading] = useState(isEdit);
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);

  const { tools } = useTools();
  const toolOptions = tools?.map((t) => ({ label: t.name, value: t.id })) ?? [];

  const form = useForm({
    initialValues: {
      name: "",
      description: "",
      panelists: [
        { ...emptySeat(), name: "Panelist 1" },
        { ...emptySeat(), name: "Panelist 2" },
      ],
      chair: { ...emptySeat(), name: "Chair", persona: "You are a neutral chair." },
      protocol: "roundtable",
      rounds: 1,
      memory: "chairSummary",
    },
    validate: {
      name: (v) => (v ? null : "Name is required"),
      chair: {
        model: (v) => (v ? null : "Chair needs a model"),
      },
    },
  });

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    getPanel(panelId)
      .then((p) => {
        form.setValues({
          name: p.name,
          description: p.description ?? "",
          panelists: (p.panelists ?? []).map((s) => ({
            ...emptySeat(),
            ...s,
            persona: s.persona ?? "",
            temperature: s.temperature ?? "",
            toolIds: s.toolIds ?? [],
          })),
          chair: {
            ...emptySeat(),
            ...p.chair,
            persona: p.chair?.persona ?? "",
            temperature: p.chair?.temperature ?? "",
          },
          protocol: p.protocol ?? "roundtable",
          rounds: p.rounds ?? 1,
          memory: p.memory ?? "chairSummary",
        });
      })
      .catch((err) =>
        notifications.show({ title: "Error", message: err.message, color: "red" })
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelId]);

  const handleSubmit = async (values) => {
    try {
      const dto = toDto(values);
      if (isEdit) await updatePanel(panelId, dto);
      else await createPanel(dto);
      setLocation("/panels/manage");
    } catch (err) {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    }
  };

  const runEstimate = async () => {
    setEstimating(true);
    try {
      setEstimate(await estimatePanel(toDto(form.getValues())));
    } catch (err) {
      notifications.show({ title: "Error", message: err.message, color: "red" });
    } finally {
      setEstimating(false);
    }
  };

  if (loading) return <Loader />;

  const values = form.getValues();
  const seats = values.panelists;
  const isDebate = values.protocol === "debate";

  return (
    <Container size="md" px={0}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Title order={2}>{isEdit ? "Edit panel" : "New panel"}</Title>

          <TextInput
            label="Name"
            placeholder="Hard Business Questions"
            {...form.getInputProps("name")}
          />
          <Textarea
            label="Description"
            placeholder="What this panel is for"
            autosize
            minRows={1}
            {...form.getInputProps("description")}
          />

          <Divider label="Panelists (seats)" labelPosition="center" />
          <Text size="sm" c="dimmed">
            Each seat is one model plus a persona. Round 1 is blind — seats answer
            independently, which is what makes a diverse panel valuable.
          </Text>

          {seats.map((seat, i) => (
            <Paper key={i} withBorder p="md" radius="md">
              <Group justify="space-between" mb="xs">
                <Text fw={500}>Seat {i + 1}</Text>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  disabled={seats.length <= 1}
                  onClick={() => form.removeListItem("panelists", i)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
              <Stack gap="xs">
                <TextInput
                  label="Persona name"
                  placeholder="Real-estate lawyer"
                  description="Shown to the chair; the model vendor is hidden from peers."
                  {...form.getInputProps(`panelists.${i}.name`)}
                />
                <ModelSelect
                  label="Model"
                  {...form.getInputProps(`panelists.${i}.model`)}
                />
                <Textarea
                  label="Persona (system prompt)"
                  placeholder="You are a real-estate lawyer focused on..."
                  autosize
                  minRows={2}
                  {...form.getInputProps(`panelists.${i}.persona`)}
                />
                <NumberInput
                  label="Temperature (optional)"
                  min={0}
                  max={2}
                  step={0.1}
                  decimalScale={2}
                  allowDecimal
                  {...form.getInputProps(`panelists.${i}.temperature`)}
                />
                <MultiSelect
                  label="Tools (optional)"
                  data={toolOptions}
                  disabled={toolOptions.length === 0}
                  placeholder={
                    toolOptions.length === 0
                      ? "No tools defined yet — add them under Tools"
                      : "Give this seat web / API access"
                  }
                  description="Let this seat search the web or call tools while it answers. Adds cost + latency per round and isn't counted in the estimate."
                  searchable
                  clearable
                  {...form.getInputProps(`panelists.${i}.toolIds`)}
                />
                {isDebate && (
                  <Select
                    label="Role"
                    data={ROLE_OPTIONS}
                    allowDeselect={false}
                    description="A devil's advocate always argues the strongest counter-case, to fight groupthink."
                    {...form.getInputProps(`panelists.${i}.kind`)}
                  />
                )}
              </Stack>
            </Paper>
          ))}

          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={() =>
              form.insertListItem("panelists", {
                ...emptySeat(),
                name: `Panelist ${seats.length + 1}`,
              })
            }
          >
            Add seat
          </Button>

          <Divider label="Chair (synthesizer)" labelPosition="center" />
          <Text size="sm" c="dimmed">
            The chair reads all answers and produces the single response, surfacing
            dissent. Use a strong, schema-capable model — ideally not one of the
            seats, to avoid self-preference. The chair synthesizes the panel's
            transcript and doesn't run tools of its own.
          </Text>
          <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
              <ModelSelect
                label="Chair model"
                {...form.getInputProps("chair.model")}
              />
              <Textarea
                label="Chair persona (optional)"
                autosize
                minRows={2}
                {...form.getInputProps("chair.persona")}
              />
            </Stack>
          </Paper>

          <Divider label="Protocol" labelPosition="center" />
          <Select
            label="Deliberation protocol"
            data={PROTOCOL_OPTIONS}
            allowDeselect={false}
            value={values.protocol}
            onChange={(v) => {
              form.setFieldValue("protocol", v);
              // Debate needs at least two rounds to mean anything; bump a stale "1" up.
              if (v === "debate" && (values.rounds ?? 1) < 2)
                form.setFieldValue("rounds", 2);
            }}
          />
          {isDebate && (
            <>
              <NumberInput
                label="Rounds"
                description={
                  `Round 1 is blind; each later round shows every seat the anonymized ` +
                  `peer answers to rebut or revise. The chair can stop early once the ` +
                  `panel converges. Capped at ${MAX_DEBATE_ROUNDS}.`
                }
                min={2}
                max={MAX_DEBATE_ROUNDS}
                clampBehavior="strict"
                allowDecimal={false}
                {...form.getInputProps("rounds")}
              />
              <Text size="xs" c="dimmed">
                Cost grows quickly — each round every seat reads every other seat.
                Use the estimate below before committing.
              </Text>
            </>
          )}

          <Divider label="Cost" labelPosition="center" />
          <Group>
            <Button
              variant="default"
              leftSection={<IconCalculator size={16} />}
              loading={estimating}
              onClick={runEstimate}
            >
              Estimate cost per turn
            </Button>
          </Group>
          {estimate && <EstimatePanel estimate={estimate} />}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setLocation("/panels/manage")}>
              Cancel
            </Button>
            <Button type="submit">{isEdit ? "Save" : "Create panel"}</Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}

function EstimatePanel({ estimate }) {
  return (
    <Paper withBorder p="md" radius="md">
      <Group justify="space-between">
        <Text fw={600}>~{usd(estimate.expectedUsd)} / turn</Text>
        <Text size="sm" c="dimmed">
          range {usd(estimate.lowUsd)} – {usd(estimate.highUsd)}
        </Text>
      </Group>
      <Text size="xs" c="dimmed" mt={4}>
        ~{estimate.totalTokensIn.toLocaleString()} in /{" "}
        {estimate.totalTokensOut.toLocaleString()} out tokens · billed{" "}
        {estimate.expectedCredits.toLocaleString()} credits. Rough estimate for a
        hard question; actual cost is tracked per run.
      </Text>

      {estimate.warnings?.length > 0 && (
        <Alert color="yellow" mt="sm" variant="light">
          <Stack gap={2}>
            {estimate.warnings.map((w, i) => (
              <Text key={i} size="xs">
                {w}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      <Table mt="sm" fz="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Seat</Table.Th>
            <Table.Th>Model</Table.Th>
            <Table.Th>~$</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {estimate.seats.map((s, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                {s.seatName === "Chair" ? (
                  <Badge size="xs" variant="light" color="grape">
                    Chair
                  </Badge>
                ) : (
                  s.seatName
                )}
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  {s.model}
                </Text>
              </Table.Td>
              <Table.Td>{usd(s.expectedUsd)}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}
